// src/App.js
// ------------------------------------------------------------
// ✅ BYN Landing (2025 Final Edition)
// Assets required (place under /public/assets/hero/):
//   hero1.png  -> Hero (Book Your Need)
//   hero2.png  -> Bidding & Transparency
//   hero3.png  -> Health & Beauty
//   hero4.png  -> BYN Rides
//   bid-mock.png -> Bidding Mockup
//   ride-mock.png -> Ride Mockup
//
// Tailwind + Framer Motion required.
// ------------------------------------------------------------
import React from "react";
import { motion } from "framer-motion";
import Logo from "./byn-logo.jpeg";
const APP_URL = "https://app.bookyourneed.com";
const WORKER_URL = "https://worker.bookyourneed.com";

// Motion helpers
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, delay },
});

const fade = (delay = 0) => ({
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, delay },
});

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#f8fbff] text-[#111827] font-sans antialiased">{children}</div>
);

const Section = ({ bg, overlay, children, className = "", tight = false }) => (
  <section className={`relative ${tight ? "py-16 md:py-20" : "py-20 md:py-28"} ${className}`}>
    {bg && (
      <div className="absolute inset-0">
        <img src={bg} alt="" className="w-full h-full object-cover object-center" loading="eager" />
      </div>
    )}
    {overlay && <div className={`absolute inset-0 ${overlay}`} />}
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
  </section>
);

const Pill = ({ children }) => (
  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium bg-white/70 text-[#0f172a] ring-1 ring-white/60 backdrop-blur">
    {children}
  </span>
);

const Card = ({ children, className = "" }) => (
  <div
    className={
      "bg-white/85 backdrop-blur-sm rounded-2xl shadow-[0_6px_24px_rgba(2,6,23,0.06)] ring-1 ring-black/5 " +
      className
    }
  >
    {children}
  </div>
);

const Mini = ({ title, text, icon }) => (
  <motion.div
    {...fadeUp(0.05)}
    className="flex items-start gap-4 p-6 bg-white/80 rounded-xl ring-1 ring-black/5 shadow-sm"
  >
    <div className="text-2xl">{icon}</div>
    <div>
      <div className="font-semibold text-[#0f172a]">{title}</div>
      <div className="text-sm text-[#475569] mt-1">{text}</div>
    </div>
  </motion.div>
);

// ---------------------------- MAIN PAGE ----------------------------
export default function App() {
  return (
    <Shell>
      {/* NAV */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
  src={Logo}
  alt="BYN Logo"
  className="h-16 w-auto object-contain"
/>

            <span className="font-extrabold tracking-tight text-xl text-[#0f172a]">
              Book Your Need
            </span>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <Pill>Verified workers</Pill>
            <Pill>Secure payments</Pill>
            <Pill>Canada-wide</Pill>
          </div>
        </div>
      </header>

      {/* HERO */}
      <Section
        bg="/assets/hero/hero1.png"
        overlay="bg-gradient-to-b from-black/35 via-black/25 to-white/0"
        className="h-[86vh] md:h-[92vh] flex items-center"
      >
        <div className="max-w-3xl">
          <motion.h1
            {...fadeUp(0)}
            className="text-5xl md:text-6xl font-extrabold leading-tight tracking-tight"
          >
            <span className="bg-gradient-to-r from-blue-600 to-cyan-400 bg-clip-text text-transparent">
              Book Your Need
            </span>
          </motion.h1>

          <motion.p {...fadeUp(0.1)} className="text-lg md:text-xl text-white/90 mt-4">
            Find trusted professionals near you — anytime, anywhere.
          </motion.p>

          <motion.div {...fadeUp(0.2)} className="mt-8 flex flex-wrap gap-3">
            <a
              href={APP_URL}
              className="px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg shadow-blue-600/20 transition"
            >
              Open the Customer App
            </a>
            <a
              href={WORKER_URL}
              className="px-6 py-3 rounded-lg bg-white/90 hover:bg-white text-blue-700 font-semibold ring-1 ring-blue-200 transition backdrop-blur"
            >
              Open the Worker App
            </a>
          </motion.div>
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section tight className="bg-[#f8fbff]">
        <motion.h2
          {...fadeUp(0)}
          className="text-3xl md:text-4xl font-bold text-center text-blue-700"
        >
          How BYN Works
        </motion.h2>

        <div className="mt-10 grid sm:grid-cols-3 gap-6 max-w-6xl mx-auto">
          <Mini icon="📝" title="Post Your Need" text="Describe your job and timing." />
          <Mini icon="💬" title="Bids & Chat" text="Compare price, ETA, and ratings." />
          <Mini icon="💳" title="Done & Pay Securely" text="Funds are held safely by BYN." />
        </div>
      </Section>

      {/* BIDDING & TRANSPARENCY */}
      <Section bg="/assets/hero/hero2.png" overlay="bg-white/65" className="bg-[#eaf2ff]">
        <div className="grid md:grid-cols-2 gap-8 items-stretch">
          <Card className="p-8">
            <motion.h3 {...fadeUp(0)} className="text-2xl font-bold text-blue-700">
              Transparent bids. You choose the best fit.
            </motion.h3>
            <motion.ul {...fadeUp(0.05)} className="mt-5 space-y-3 text-[#334155]">
              <li>• View worker <b>price</b>, <b>ETA</b>, <b>ratings</b>, and <b>certifications</b>.</li>
              <li>• In-app <b>chat</b> before assigning a job.</li>
              <li>• Funds held by BYN until you approve completion.</li>
              <li>• <b>Auto-release</b> after the completion window (or dispute via support).</li>
            </motion.ul>
          </Card>

          <Card className="p-6 flex items-center justify-center">
            <img
              src="/assets/hero/bid-mock.png"
              alt="Bidding mockup"
              className="w-full h-64 md:h-80 object-contain rounded-xl shadow-lg"
            />
          </Card>
        </div>
      </Section>

      {/* HEALTH & BEAUTY */}
      <Section bg="/assets/hero/hero3.png" overlay="bg-white/70" className="bg-[#f1f5ff]">
        <motion.h2
          {...fadeUp(0)}
          className="text-3xl md:text-4xl font-bold text-center text-blue-700"
        >
          Health & Beauty — at your home
        </motion.h2>
        <motion.p
          {...fadeUp(0.05)}
          className="max-w-3xl mx-auto text-center text-[#475569] mt-4"
        >
          Makeup, manicure/pedicure, waxing, facials, and skin-care routines. Book trusted beauty
          pros who come to you — portfolios, verified IDs, real reviews.
        </motion.p>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          <Mini title="Makeup" text="Events, parties, bridal." icon="💄" />
          <Mini title="Mani/Pedi" text="At-home nail care & polish." icon="💅" />
          <Mini title="Waxing" text="Professional, hygienic service." icon="🕯️" />
          <Mini title="Facials & Skin" text="Deep clean, glow, and care." icon="🧖‍♀️" />
        </div>
      </Section>

      {/* BYN RIDES */}
      <Section
        bg="/assets/hero/hero4.png"
        overlay="bg-gradient-to-b from-black/25 via-white/55 to-white/70"
        className="bg-[#eef2ff]"
      >
        <motion.h2
          {...fadeUp(0)}
          className="text-3xl md:text-4xl font-bold text-center text-blue-700"
        >
          BYN Rides — affordable, shared travel
        </motion.h2>

        <div className="mt-10 grid md:grid-cols-2 gap-8 items-stretch">
          <Card className="p-8">
            <motion.p {...fadeUp(0.05)} className="text-[#334155]">
              See where people are travelling and carpool with verified drivers. Split fares, chat
              in-app, and track trips live.
            </motion.p>
            <motion.ul {...fadeUp(0.1)} className="mt-5 space-y-3 text-[#334155]">
              <li>• Real-time ride listings</li>
              <li>• Driver profiles & ratings</li>
              <li>• In-app chat & escrowed payments</li>
            </motion.ul>
          </Card>

          <Card className="p-6 flex items-center justify-center">
            <img
              src="/assets/hero/ride-mock.png"
              alt="Ride mockup"
              className="w-full h-64 md:h-80 object-contain rounded-xl shadow-lg"
            />
          </Card>
        </div>
      </Section>

      {/* TRUST STRIP */}
      <Section tight className="bg-white">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          {[
            { label: "Verified Workers", emoji: "✅" },
            { label: "Secure Payments", emoji: "🔒" },
            { label: "Quality Tracking", emoji: "🧰" },
            { label: "Canada-Wide", emoji: "🇨🇦" },
          ].map((t, i) => (
            <motion.div
              key={t.label}
              {...fadeUp(i * 0.05)}
              className="bg-white rounded-xl ring-1 ring-slate-200 py-6 shadow-[0_4px_16px_rgba(2,6,23,0.04)]"
            >
              <div className="text-2xl">{t.emoji}</div>
              <div className="mt-2 font-semibold text-[#0f172a]">{t.label}</div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ABOUT + HELP */}
      <Section tight className="bg-[#f8fbff]">
        <div className="grid md:grid-cols-3 gap-8">
          <Card className="p-8 md:col-span-2">
            <motion.h3 {...fadeUp(0)} className="text-2xl font-bold text-blue-700">
              Built for Canadian communities
            </motion.h3>
            <motion.p {...fadeUp(0.05)} className="mt-4 text-[#334155]">
              Book Your Need connects households and businesses with local professionals across
              Canada. We’re building fair opportunities for workers and the fastest way for
              customers to get things done — with transparency, safety, and real human support.
            </motion.p>
          </Card>

          <Card className="p-8">
            <motion.h3 {...fadeUp(0)} className="text-2xl font-bold text-blue-700">
              Help & Support
            </motion.h3>
            <motion.p {...fadeUp(0.05)} className="mt-3 text-[#334155]">
              Open the Help section in your BYN app to chat with support, report an issue, or read
              FAQs. We’re here for customers and workers.
            </motion.p>
          </Card>
        </div>
      </Section>

      {/* FOOTER */}
      <footer className="py-10 text-center text-[#64748b] border-t border-slate-200/70 bg-white">
        <p className="mb-2">
          © {new Date().getFullYear()} Book Your Need Services Inc. All rights reserved.
        </p>
      </footer>
    </Shell>
  );
}
